import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import AudioManager, { type FrequencyData } from '../../services/AudioManager';
import './AudioReactiveParticles.css';

interface AudioReactiveParticlesProps {
  // Single audio (legacy support)
  audioFile?: File;
  audioURL?: string;
  onAudioEnd?: () => void;
  
  // Two-audio sequence
  audioURL1?: string;
  audioURL2?: string;
  onAudio1End?: () => void;
  onAudio2End?: () => void;
  
  // Control props
  initialPhase?: 'scattered' | 'background';
  triggerFormation?: boolean;
  /** Live TTS stream frequency data (e.g. Gemini PCM player) */
  getExternalFrequencyData?: () => FrequencyData | null;
  /** When false after stream playback, particles scatter (URL audio uses AudioManager end instead) */
  streamPlaybackActive?: boolean;
  
  // Background mode settings
  backgroundMode?: boolean;

  /** Centered on the particle sphere (does not affect sphere layout) */
  centerOverlay?: React.ReactNode;

  className?: string;
}

type AnimationPhase = 'background' | 'scattered' | 'forming' | 'playing' | 'playing1' | 'playing2' | 'scattering' | 'ended';

const AudioReactiveParticles: React.FC<AudioReactiveParticlesProps> = ({
  audioFile,
  audioURL,
  onAudioEnd,
  audioURL1,
  audioURL2,
  onAudio1End,
  onAudio2End,
  initialPhase = 'scattered',
  triggerFormation = false,
  getExternalFrequencyData,
  streamPlaybackActive = false,
  backgroundMode = false,
  centerOverlay,
  className
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const particlesRef = useRef<THREE.Points | null>(null);
  const materialRef = useRef<THREE.PointsMaterial | null>(null);
  const geometryRef = useRef<THREE.BufferGeometry | null>(null);
  const audioManager1Ref = useRef<AudioManager | null>(null);
  const audioManager2Ref = useRef<AudioManager | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timeRef = useRef<number>(0);
  const phaseRef = useRef<AnimationPhase>(initialPhase);
  const phaseStartTimeRef = useRef<number>(0);
  const hasTriggeredFormationRef = useRef<boolean>(false);
  const streamPlaybackWasActiveRef = useRef<boolean>(false);
  
  // Position arrays
  const scatteredPositionsRef = useRef<Float32Array | null>(null);
  const spherePositionsRef = useRef<Float32Array | null>(null);
  
  // Smoothed audio values for fluid animation
  const smoothedAudioRef = useRef({ low: 0, mid: 0, high: 0, overall: 0 });
  
  // Particle config
  const particleCountRef = useRef<number>(8000);
  const radiusRef = useRef<number>(1.8);
  
  // Store callbacks in refs to avoid useEffect re-runs
  const onAudioEndRef = useRef(onAudioEnd);
  const onAudio1EndRef = useRef(onAudio1End);
  const onAudio2EndRef = useRef(onAudio2End);
  const getExternalFrequencyDataRef = useRef(getExternalFrequencyData);
  const streamPlaybackActiveRef = useRef(streamPlaybackActive);
  
  // Keep refs updated with latest callbacks
  useEffect(() => {
    onAudioEndRef.current = onAudioEnd;
    onAudio1EndRef.current = onAudio1End;
    onAudio2EndRef.current = onAudio2End;
    getExternalFrequencyDataRef.current = getExternalFrequencyData;
    streamPlaybackActiveRef.current = streamPlaybackActive;
  }, [onAudioEnd, onAudio1End, onAudio2End, getExternalFrequencyData, streamPlaybackActive]);
  
  const [phase, setPhase] = useState<AnimationPhase>(initialPhase);

  // Effect to handle external trigger for formation
  useEffect(() => {
    if (triggerFormation && !hasTriggeredFormationRef.current) {
      const phase = phaseRef.current;
      if (phase === 'scattered' || phase === 'background' || phase === 'ended') {
        hasTriggeredFormationRef.current = true;
        phaseRef.current = 'forming';
        phaseStartTimeRef.current = timeRef.current;
        setPhase('forming');
      }
    }
  }, [triggerFormation]);

  useEffect(() => {
    if (streamPlaybackActive) {
      streamPlaybackWasActiveRef.current = true;
      return;
    }
    if (!streamPlaybackWasActiveRef.current) {
      return;
    }
    streamPlaybackWasActiveRef.current = false;
    const phase = phaseRef.current;
    if (phase === 'playing' || phase === 'playing1' || phase === 'playing2') {
      phaseRef.current = 'scattering';
      phaseStartTimeRef.current = timeRef.current;
      setPhase('scattering');
    }
  }, [streamPlaybackActive]);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Initialize Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020208);
    sceneRef.current = scene;

    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;
    const aspect = width / height || 1;
    
    const camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
    camera.position.z = 4;
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: backgroundMode });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    if (backgroundMode) {
      renderer.setClearColor(0x000000, 0);
    }
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create particle system
    const particleCount = particleCountRef.current;
    const radius = radiusRef.current;
    const scatterRadius = 6;
    
    const scatteredPositions = new Float32Array(particleCount * 3);
    const spherePositions = new Float32Array(particleCount * 3);
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    // Create both scattered and sphere positions
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;

      // Scattered positions - random in a larger volume
      const scatterU = Math.random();
      const scatterV = Math.random();
      const scatterTheta = scatterU * 2.0 * Math.PI;
      const scatterPhi = Math.acos(2.0 * scatterV - 1.0);
      const scatterR = (0.5 + Math.random() * 0.5) * scatterRadius;
      
      scatteredPositions[i3] = scatterR * Math.sin(scatterPhi) * Math.cos(scatterTheta);
      scatteredPositions[i3 + 1] = scatterR * Math.sin(scatterPhi) * Math.sin(scatterTheta);
      scatteredPositions[i3 + 2] = scatterR * Math.cos(scatterPhi);

      // Sphere positions - dense cluster
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);
      const r = Math.pow(Math.random(), 0.5) * radius;

      spherePositions[i3] = r * Math.sin(phi) * Math.cos(theta);
      spherePositions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      spherePositions[i3 + 2] = r * Math.cos(phi);

      // Start at scattered positions
      positions[i3] = scatteredPositions[i3];
      positions[i3 + 1] = scatteredPositions[i3 + 1];
      positions[i3 + 2] = scatteredPositions[i3 + 2];

      // Colors: Blue gradient based on distance from center
      const normalizedDist = r / radius;
      const baseColor = new THREE.Color();
      
      const innerColor = new THREE.Color('#D1B6FA');
      const midColor = new THREE.Color('#8ABFEB');
      const outerColor = new THREE.Color('#3D35F0');
      
      if (normalizedDist < 0.4) {
        const t = normalizedDist / 0.4;
        baseColor.lerpColors(innerColor, midColor, t);
      } else {
        const t = (normalizedDist - 0.4) / 0.6;
        baseColor.lerpColors(midColor, outerColor, t);
      }
      
      const variation = (Math.random() - 0.5) * 0.1;
      baseColor.r = Math.max(0, Math.min(1, baseColor.r + variation));
      baseColor.g = Math.max(0, Math.min(1, baseColor.g + variation));
      baseColor.b = Math.max(0, Math.min(1, baseColor.b + variation));
      
      colors[i3] = baseColor.r;
      colors[i3 + 1] = baseColor.g;
      colors[i3 + 2] = baseColor.b;

      sizes[i] = (1 + Math.random() * 2) * (1.2 - normalizedDist * 0.4);
    }

    scatteredPositionsRef.current = scatteredPositions;
    spherePositionsRef.current = spherePositions;

    // Create geometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    geometryRef.current = geometry;

    // Create texture for soft particles
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.8)');
    gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0.3)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
    
    const texture = new THREE.CanvasTexture(canvas);

    // Create material - adjust for background mode
    const material = new THREE.PointsMaterial({
      size: backgroundMode ? 0.018 : 0.025,
      map: texture,
      transparent: true,
      opacity: backgroundMode ? 0.4 : 0.7,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true
    });
    materialRef.current = material;

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);
    particlesRef.current = particles;

    // Initialize audio managers
    const audioManager1 = new AudioManager();
    const audioManager2 = new AudioManager();
    audioManager1Ref.current = audioManager1;
    audioManager2Ref.current = audioManager2;

    // Load audio files
    const loadAudio = async () => {
      try {
        // Two-audio mode - load both in parallel
        const loadPromises: Promise<void>[] = [];
        
        if (audioURL1) {
          console.log('Loading audio 1:', audioURL1);
          loadPromises.push(audioManager1.loadAudioFromURL(audioURL1));
        }
        if (audioURL2) {
          console.log('Loading audio 2:', audioURL2);
          loadPromises.push(audioManager2.loadAudioFromURL(audioURL2));
        }
        
        // Single audio mode (legacy)
        if (audioFile) {
          loadPromises.push(audioManager1.loadAudioFromFile(audioFile));
        } else if (audioURL && !audioURL1) {
          loadPromises.push(audioManager1.loadAudioFromURL(audioURL));
        }
        
        await Promise.all(loadPromises);
        console.log('All audio files loaded successfully');
        console.log('Audio 1 loaded:', audioManager1.isLoaded());
        console.log('Audio 2 loaded:', audioManager2.isLoaded());
      } catch (error) {
        console.error('Failed to load audio:', error);
      }
    };

    loadAudio();

    // Handle window resize
    const handleResize = () => {
      if (!container || !camera || !renderer) return;
      
      const w = container.clientWidth || window.innerWidth;
      const h = container.clientHeight || window.innerHeight;
      
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // Easing functions
    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const easeOutExpo = (t: number): number => {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    };

    // Animation loop
    let lastTime = performance.now();
    const formingDuration = 2.0;
    const scatteringDuration = 1.5;

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      const currentTime = performance.now();
      const deltaTime = (currentTime - lastTime) * 0.001;
      lastTime = currentTime;
      
      timeRef.current += deltaTime;
      const time = timeRef.current;

      const positionAttribute = geometry.getAttribute('position') as THREE.BufferAttribute;
      const positions = positionAttribute.array as Float32Array;
      const scatteredPositions = scatteredPositionsRef.current;
      const spherePositions = spherePositionsRef.current;
      const currentPhase = phaseRef.current;

      if (!scatteredPositions || !spherePositions) return;

      const phaseTime = time - phaseStartTimeRef.current;

      // Handle different animation phases
      if (currentPhase === 'background' || currentPhase === 'scattered') {
        if (reducedMotion) {
          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            positions[i3] = scatteredPositions[i3];
            positions[i3 + 1] = scatteredPositions[i3 + 1];
            positions[i3 + 2] = scatteredPositions[i3 + 2];
          }
        } else {
          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            const ox = scatteredPositions[i3];
            const oy = scatteredPositions[i3 + 1];
            const oz = scatteredPositions[i3 + 2];
            
            const drift = backgroundMode ? 0.05 : 0.1;
            const speed = backgroundMode ? 0.15 : 0.3;
            positions[i3] = ox + Math.sin(time * speed + i * 0.01) * drift;
            positions[i3 + 1] = oy + Math.cos(time * speed * 0.83 + i * 0.02) * drift;
            positions[i3 + 2] = oz + Math.sin(time * speed * 1.17 + i * 0.015) * drift;
          }
          
          if (particles) {
            particles.rotation.y += backgroundMode ? 0.0002 : 0.0005;
          }
        }
        
      } else if (currentPhase === 'forming') {
        const progress = reducedMotion ? 1 : Math.min(phaseTime / formingDuration, 1);
        const easedProgress = reducedMotion ? 1 : easeOutExpo(progress);
        
        for (let i = 0; i < particleCount; i++) {
          const i3 = i * 3;
          positions[i3] = scatteredPositions[i3] + (spherePositions[i3] - scatteredPositions[i3]) * easedProgress;
          positions[i3 + 1] = scatteredPositions[i3 + 1] + (spherePositions[i3 + 1] - scatteredPositions[i3 + 1]) * easedProgress;
          positions[i3 + 2] = scatteredPositions[i3 + 2] + (spherePositions[i3 + 2] - scatteredPositions[i3 + 2]) * easedProgress;
        }
        
        if (!reducedMotion) {
          if (material && backgroundMode) {
            material.opacity = 0.4 + 0.3 * easedProgress;
            material.size = 0.018 + 0.007 * easedProgress;
          }
          
          if (particles) {
            particles.rotation.y += 0.003 * easedProgress;
          }
        } else if (material && backgroundMode) {
          material.opacity = 0.7;
          material.size = 0.025;
        }
        
        if (progress >= 1) {
          // Determine which audio mode to use
          if (audioURL1 || audioURL2) {
            // Two-audio mode
            phaseRef.current = 'playing1';
            setPhase('playing1');
            
            // Start audio 1
            if (audioManager1) {
              const startAudio1 = async () => {
                try {
                  audioManager1.setupAudioSource(() => {
                  // Audio 1 ended - fire callback
                  console.log('Audio 1 ended');
                  if (onAudio1EndRef.current) {
                    onAudio1EndRef.current();
                  }
                    
                    // Start audio 2 immediately if available
                    if (audioURL2 && audioManager2Ref.current) {
                      // Transition to playing2 phase
                      phaseRef.current = 'playing2';
                      setPhase('playing2');
                      
                    // Start audio 2 with a 1 second delay between audios
                    const startAudio2 = async () => {
                      const am2 = audioManager2Ref.current;
                      if (am2) {
                        try {
                          // 1 second pause between audio 1 and audio 2
                          console.log('Waiting 1 second before audio 2...');
                          await new Promise(resolve => setTimeout(resolve, 1000));
                          
                          // Wait for audio 2 to be loaded if not already
                          let attempts = 0;
                          while (!am2.isLoaded() && attempts < 50) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                            attempts++;
                          }
                          
                          if (!am2.isLoaded()) {
                            console.error('Audio 2 not loaded after waiting');
                            phaseRef.current = 'scattering';
                            phaseStartTimeRef.current = timeRef.current;
                            setPhase('scattering');
                            return;
                          }
                          
                          console.log('Setting up audio 2 source...');
                          am2.setupAudioSource(() => {
                            // Audio 2 ended - start scattering
                            console.log('Audio 2 ended, starting scatter');
                            phaseRef.current = 'scattering';
                            phaseStartTimeRef.current = timeRef.current;
                            setPhase('scattering');
                          });
                          
                          console.log('Playing audio 2...');
                          await am2.play();
                          am2.startAnalysis();
                          console.log('Audio 2 started successfully');
                        } catch (error) {
                          console.error('Failed to start audio 2:', error);
                          // Fallback: start scattering if audio 2 fails
                          phaseRef.current = 'scattering';
                          phaseStartTimeRef.current = timeRef.current;
                          setPhase('scattering');
                        }
                      }
                    };
                    startAudio2();
                    } else {
                      // No audio 2, start scattering
                      phaseRef.current = 'scattering';
                      phaseStartTimeRef.current = timeRef.current;
                      setPhase('scattering');
                    }
                  });
                  console.log('Playing audio 1...');
                  await audioManager1.play();
                  audioManager1.startAnalysis();
                  console.log('Audio 1 started successfully');
                } catch (error) {
                  console.error('Failed to start audio 1:', error);
                }
              };
              startAudio1();
            }
          } else if (streamPlaybackActiveRef.current && getExternalFrequencyDataRef.current) {
            phaseRef.current = 'playing';
            setPhase('playing');
          } else {
            // Single audio mode (legacy)
            phaseRef.current = 'playing';
            setPhase('playing');
            
            if (audioManager1) {
              const startLegacyAudio = async () => {
                try {
                  // Wait for audio to be loaded (same as two-audio mode)
                  let attempts = 0;
                  while (!audioManager1.isLoaded() && attempts < 50) {
                    console.log(`Waiting for audio to load... attempt ${attempts + 1}/50`);
                    await new Promise(resolve => setTimeout(resolve, 100));
                    attempts++;
                  }
                  
                  if (!audioManager1.isLoaded()) {
                    console.error('Audio not loaded after waiting 5 seconds');
                    // Fallback: start scattering if audio fails to load
                    phaseRef.current = 'scattering';
                    phaseStartTimeRef.current = timeRef.current;
                    setPhase('scattering');
                    return;
                  }
                  
                  console.log('Audio loaded, setting up source...');
                  audioManager1.setupAudioSource(() => {
                    phaseRef.current = 'scattering';
                    phaseStartTimeRef.current = timeRef.current;
                    setPhase('scattering');
                  });
                  await audioManager1.play();
                  audioManager1.startAnalysis();
                  console.log('Audio playback started successfully');
                } catch (error) {
                  console.error('Failed to start audio:', error);
                  // Fallback: start scattering on error
                  phaseRef.current = 'scattering';
                  phaseStartTimeRef.current = timeRef.current;
                  setPhase('scattering');
                }
              };
              startLegacyAudio();
            }
          }
        }
        
      } else if (currentPhase === 'playing' || currentPhase === 'playing1' || currentPhase === 'playing2') {
        if (reducedMotion) {
          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            positions[i3] = spherePositions[i3];
            positions[i3 + 1] = spherePositions[i3 + 1];
            positions[i3 + 2] = spherePositions[i3 + 2];
          }
          if (particles) {
            particles.rotation.y += 0.0003;
          }
        } else {
          // Get active audio manager
          const activeAudioManager = currentPhase === 'playing2' 
            ? audioManager2Ref.current 
            : audioManager1Ref.current;
          
          // Get raw audio data
          let rawLow = 0, rawMid = 0, rawHigh = 0, rawOverall = 0;
          let hasAudioData = false;
          
          if (activeAudioManager) {
            const useStreamFreq =
              streamPlaybackActiveRef.current && getExternalFrequencyDataRef.current;
            const externalFreq = useStreamFreq
              ? getExternalFrequencyDataRef.current?.()
              : null;
            if (externalFreq) {
              rawLow = externalFreq.low;
              rawMid = externalFreq.mid;
              rawHigh = externalFreq.high;
              rawOverall = externalFreq.overall;
              hasAudioData = rawOverall > 0.01;
            } else {
              const freqData = activeAudioManager.getFrequencyData();
              rawLow = freqData.low;
              rawMid = freqData.mid;
              rawHigh = freqData.high;
              rawOverall = freqData.overall;
              hasAudioData = rawOverall > 0.01;
            }
          }

          // Smooth the audio values - use slower decay when no audio data (transition period)
          const smoothed = smoothedAudioRef.current;
          
          if (hasAudioData) {
            const smoothing = 0.35;
            smoothed.low += (rawLow - smoothed.low) * smoothing;
            smoothed.mid += (rawMid - smoothed.mid) * smoothing;
            smoothed.high += (rawHigh - smoothed.high) * smoothing;
            smoothed.overall += (rawOverall - smoothed.overall) * smoothing;
          } else {
            const decayRate = 0.02;
            smoothed.low *= (1 - decayRate);
            smoothed.mid *= (1 - decayRate);
            smoothed.high *= (1 - decayRate);
            smoothed.overall *= (1 - decayRate);
          }

          const { low, mid, high, overall } = smoothed;
          
          const baselineBreath = Math.sin(time * 0.8) * 0.02 + 1;
          const baselineNoise = 0.05;

          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            
            const ox = spherePositions[i3];
            const oy = spherePositions[i3 + 1];
            const oz = spherePositions[i3 + 2];
            
            const dist = Math.sqrt(ox * ox + oy * oy + oz * oz);
            const normalizedDist = dist / radius;
            const sensitivity = Math.pow(1 - normalizedDist, 1.5) * 0.85 + 0.15;
            
            const pulseFactor = baselineBreath + low * 0.8 * sensitivity;
            const displacement = high * 0.5 * sensitivity;
            
            let x = ox * pulseFactor;
            let y = oy * pulseFactor;
            let z = oz * pulseFactor;
            
            const noiseTime = time * 0.4;
            const noiseScale = (baselineNoise + mid * 0.6) * sensitivity;
            
            const phaseX = i * 0.001;
            const phaseY = i * 0.0013;
            const phaseZ = i * 0.0017;
            
            x += Math.sin(noiseTime + phaseX) * noiseScale * 0.2;
            y += Math.cos(noiseTime * 1.1 + phaseY) * noiseScale * 0.2;
            z += Math.sin(noiseTime * 0.9 + phaseZ) * noiseScale * 0.2;
            
            if (dist > 0) {
              const radialWave = Math.sin(time * 2 + i * 0.05) * 0.5 + 0.5;
              const radialPush = displacement * radialWave;
              x += (ox / dist) * radialPush;
              y += (oy / dist) * radialPush;
              z += (oz / dist) * radialPush;
            }
            
            const baseSwirl = 0.01;
            const swirlAngle = time * 0.3 + overall * 0.5;
            const swirlAmount = (baseSwirl + overall * 0.05) * sensitivity;
            const cosSwirl = Math.cos(swirlAngle + i * 0.01);
            const sinSwirl = Math.sin(swirlAngle + i * 0.01);
            const tempX = x;
            x = x * (1 - swirlAmount) + (cosSwirl * tempX - sinSwirl * z) * swirlAmount;
            z = z * (1 - swirlAmount) + (sinSwirl * tempX + cosSwirl * z) * swirlAmount;
            
            positions[i3] = x;
            positions[i3 + 1] = y;
            positions[i3 + 2] = z;
          }
          
          if (particles) {
            particles.rotation.y += 0.001;
            particles.rotation.x = Math.sin(time * 0.05) * 0.05;
          }

          if (material) {
            material.size = 0.025 + smoothed.mid * 0.012;
            material.opacity = 0.7 + smoothed.overall * 0.25;
          }
        }
        
      } else if (currentPhase === 'scattering') {
        const progress = reducedMotion ? 1 : Math.min(phaseTime / scatteringDuration, 1);
        const easedProgress = reducedMotion ? 1 : easeInOutCubic(progress);
        
        for (let i = 0; i < particleCount; i++) {
          const i3 = i * 3;
          
          const sx = spherePositions[i3];
          const sy = spherePositions[i3 + 1];
          const sz = spherePositions[i3 + 2];
          
          const tx = scatteredPositions[i3];
          const ty = scatteredPositions[i3 + 1];
          const tz = scatteredPositions[i3 + 2];
          
          positions[i3] = sx + (tx - sx) * easedProgress;
          positions[i3 + 1] = sy + (ty - sy) * easedProgress;
          positions[i3 + 2] = sz + (tz - sz) * easedProgress;
        }
        
        if (!reducedMotion) {
          if (particles) {
            particles.rotation.y += 0.002 * (1 - easedProgress);
          }
          
          if (material) {
            material.opacity = 0.7 * (1 - easedProgress * 0.3);
          }
        }
        
        if (progress >= 1) {
          phaseRef.current = 'ended';
          setPhase('ended');
          hasTriggeredFormationRef.current = false;
          
          // Fire appropriate callback using refs
          if (audioURL1 || audioURL2) {
            if (onAudio2EndRef.current) {
              onAudio2EndRef.current();
            }
          } else if (onAudioEndRef.current) {
            onAudioEndRef.current();
          }
        }
        
      } else if (currentPhase === 'ended') {
        if (reducedMotion) {
          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            positions[i3] = scatteredPositions[i3];
            positions[i3 + 1] = scatteredPositions[i3 + 1];
            positions[i3 + 2] = scatteredPositions[i3 + 2];
          }
        } else {
          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            const ox = scatteredPositions[i3];
            const oy = scatteredPositions[i3 + 1];
            const oz = scatteredPositions[i3 + 2];
            
            const drift = 0.08;
            positions[i3] = ox + Math.sin(time * 0.2 + i * 0.01) * drift;
            positions[i3 + 1] = oy + Math.cos(time * 0.15 + i * 0.02) * drift;
            positions[i3 + 2] = oz + Math.sin(time * 0.25 + i * 0.015) * drift;
          }
          
          if (particles) {
            particles.rotation.y += 0.0003;
          }
        }
      }

      positionAttribute.needsUpdate = true;
      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (audioManager1Ref.current) {
        audioManager1Ref.current.cleanup();
      }
      if (audioManager2Ref.current) {
        audioManager2Ref.current.cleanup();
      }

      if (renderer && container && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }

      if (geometry) geometry.dispose();
      if (material) material.dispose();
      if (texture) texture.dispose();
      if (renderer) renderer.dispose();
    };
  // Note: Callbacks are stored in refs to prevent effect re-runs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFile, audioURL, audioURL1, audioURL2, initialPhase, backgroundMode]);

  // Handle click to start formation (only in standalone mode without external trigger)
  const handleStart = () => {
    if (triggerFormation) return; // External trigger mode, don't respond to clicks
    if (phaseRef.current !== 'scattered' && phaseRef.current !== 'background') return;
    
    phaseRef.current = 'forming';
    phaseStartTimeRef.current = timeRef.current;
    setPhase('forming');
  };

  return (
    <div 
      ref={containerRef} 
      className={`audio-reactive-particles ${backgroundMode ? 'background-mode' : ''} ${className || ''}`}
      onClick={handleStart}
    >
      {centerOverlay ? (
        <div className="audio-particles-center-overlay">{centerOverlay}</div>
      ) : null}
    </div>
  );
};

export default AudioReactiveParticles;
